import { useEffect, useRef, useState } from 'react';
import { ordersApi } from '../api';

// While the app is open, check for new orders and announce them
const POLL_MS = 30000;
const STORAGE_KEY = 'order-alerts';

const readEnabled = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
};

// Two short rising notes
const chime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const audio = new Ctx();
    [660, 880].forEach((frequency, i) => {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, audio.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.2, audio.currentTime + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + i * 0.18 + 0.16);
      osc.connect(gain).connect(audio.destination);
      osc.start(audio.currentTime + i * 0.18);
      osc.stop(audio.currentTime + i * 0.18 + 0.17);
    });
  } catch {
    // No sound available
  }
};

// onNew(orders) is called with orders that arrived since the last check. Returns
// { enabled, toggle, activeCount }. Turning alerts on asks for permission to show notifications.
export function useOrderAlerts(onNew) {
  const [enabled, setEnabled] = useState(readEnabled);
  const [activeCount, setActiveCount] = useState(0);
  const known = useRef(null);
  const handler = useRef(onNew);
  handler.current = onNew;

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const active = await ordersApi.getActive();
        if (stopped) return;
        setActiveCount(active.length);
        const ids = new Set(active.map(o => o.id));
        // The first check only learns what's already there
        if (known.current) {
          const fresh = active.filter(o => !known.current.has(o.id) && o.status === 'pending');
          if (fresh.length && enabled) {
            chime();
            handler.current(fresh);
            if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
              new Notification(fresh.length === 1 ? `New order #${fresh[0].order_number}` : `${fresh.length} new orders`, {
                body: 'Ready to pick',
                tag: 'new-orders',
              });
            }
          }
        }
        known.current = ids;
      } catch {
        // Offline for a moment; try again next time
      }
    };
    check();
    const timer = setInterval(check, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [enabled]);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
    } catch {
      // Remembered for this visit only
    }
    if (next && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  };

  return { enabled, toggle, activeCount };
}
